import { z, ZodType } from 'zod';
import { getCardType } from '@/customercenter-ui/utils/crediCardUtils';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';

type FormData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  streetName?: string;
  additionalAddressInfo?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  newPassword?: string;
  cardNumber?: string;
  expDate?: string;
  securityCode?: string;
  loginPassword?: string;
  orderNumber?: string;
  dob?: string;
  ssn?: string;
  creditAccountId?: string;
  creditAccountIdOptional?: string;
  requestPurpose?: string;
  forgotEmail?: string;
  checkDefaultAddress?: boolean;
  currentPassword?: string;
};
let cardType = mapLocaleToMeaningfulFormat().unknown;
export const UserSchema: ZodType<FormData> = z.object({
  forgotEmail: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyEmailErrorMessage })
    .email(mapLocaleToMeaningfulFormat().emailRequired)
    .optional(),
  firstName: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyFirstNameErrorMessage })
    .max(40, { message: 'Max 40 characters' })
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9\s'-]*[a-zA-Z0-9]?\s*$/, { message: 'Please enter valid first name' })
    .refine((value) => value.replace(/[^0-9a-zA-Z\s'-]/g, ''), { message: 'Invalid character' })
    .transform((value) => value.trim())
    .optional(),

  lastName: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyLastNameErrorMessage })
    .max(40, { message: 'Max 40 characters' })
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9\s'-]*[a-zA-Z0-9]?\s*$/, { message: 'Please enter valid last name' })
    .refine((value) => value.replace(/[^0-9a-zA-Z\s'-]/g, ''), { message: 'Invalid character' })
    .transform((value) => value.trim())
    .optional(),

  email: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyEmailErrorMessage })
    .email('Please enter a valid email address')
    .optional(),

  catalogEmail: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Please enter a valid email address',
    }),

  loginPassword: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().enterPassword })
    .regex(/^(?=.*[^\s]).+$/, { message: mapLocaleToMeaningfulFormat().passwordRequired })
    .optional(),

  currentPassword: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().currentPaswordErrorMessage })
    .regex(/^(?=.*[^\s]).+$/, { message: mapLocaleToMeaningfulFormat().passwordRequired })
    .optional(),
  orderNumber: z.string().min(1, { message: mapLocaleToMeaningfulFormat().orderRequired }).optional(),
  password: z
    .string()
    .min(8, { message: 'Please enter a valid password' })
    .regex(/\d/g, { message: 'Need a digit' })
    .regex(/[a-zA-Z]/g, {
      message: 'Need a letter',
    })
    .optional(),
  newPassword: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().enterPassword })
    .regex(/^\S*$/, { message: mapLocaleToMeaningfulFormat().passwordRequired })
    .regex(/\d/g)
    .regex(/[a-zA-Z]/g)
    .min(8)
    .optional(),

  streetName: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyAddress1 })
    .max(30, { message: mapLocaleToMeaningfulFormat().addressLineMaxLength })
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9\s',-]*(?:\s*[a-zA-Z0-9][a-zA-Z0-9\s',-]*)*\s*$/, {
      message: mapLocaleToMeaningfulFormat().addressMessage,
    })
    .refine((value) => value.replace(/[^0-9a-zA-Z\s',-]/g, ''), { message: mapLocaleToMeaningfulFormat().invalidChar })
    .transform((value) => value.trim())
    .optional(),

  additionalAddressInfo: z
    .string()
    .max(30, { message: 'Maximum 30 characters are allowed' })
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: 'Invalid character' },
    )
    .optional(),

  city: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyCity })
    .max(30, { message: 'Maximum 30 characters are allowed' })
    .regex(/^[a-zA-Z\s\-,.;'&/()]+$/, { message: 'Please enter a valid city name' })
    .transform((value) => value.replace(/[^a-zA-Z\s\-,.;'&/()]/g, ''))
    .transform((value) => value.trim())
    .optional(),

  state: z.string().min(1, { message: mapLocaleToMeaningfulFormat().emptyState }).max(50).optional(),
  country: z.string().optional(),
  postalCode: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().emptyzipcode })
    .refine(
      (value) => {
        const sanitizedValue = value.replace(/\D/g, '');
        const isValidLength =
          sanitizedValue.length === 5 || (sanitizedValue.length >= 9 && sanitizedValue.length <= 10);
        const isNotAllZeros = !/^0+$/.test(sanitizedValue);
        return isValidLength && isNotAllZeros;
      },
      { message: mapLocaleToMeaningfulFormat().invalidPostalCode },
    )
    .optional(),
  phone: z
    .string()
    .min(10, { message: mapLocaleToMeaningfulFormat().emptyphone })
    .refine((value) => value.replace(/\D/g, '').length >= 10, {
      message: mapLocaleToMeaningfulFormat().emptyphone,
    })
    .refine((value) => !/^0+$/.test(value.replace(/\D/g, '')), {
      message: mapLocaleToMeaningfulFormat().invalidPhoneNumberMessage,
    })
    .optional(),
  cardNumber: z
    .string()
    .refine(
      (value) => {
        const cleanedValue = value.replace(/\D/g, '');
        const length = cleanedValue.length;
        return length >= 1;
      },
      { message: mapLocaleToMeaningfulFormat().emptyCreditCardNumber },
    )
    .refine(
      (value) => {
        const cleanedValue = value.replace(/\D/g, '');
        const length = cleanedValue.length;
        return length === 15 || length === 16;
      },
      { message: mapLocaleToMeaningfulFormat().invalidCreditCardNumber },
    )
    .refine(
      (value) => {
        const cleanedValue = value.replace(/\D/g, '');
        cardType = getCardType(cleanedValue);
        return cardType !== mapLocaleToMeaningfulFormat().unknown;
      },
      { message: mapLocaleToMeaningfulFormat().invalidCreditCardNumber },
    )
    .optional(),
  expDate: z
    .string()
    .refine(
      (value) => {
        if (!value) {
          return false;
        }
        return true;
      },
      { message: mapLocaleToMeaningfulFormat().invalidExpDate },
    )
    .refine(
      (value) => {
        if (!value) {
          return false;
        }

        const [monthStr] = value.split('/');

        const month = parseInt(monthStr, 10);
        if (isNaN(month) || month < 1 || month > 12) {
          return false;
        }

        return true;
      },
      { message: mapLocaleToMeaningfulFormat().invalidExpDate },
    )
    .refine(
      (value) => {
        if (!value) {
          return false;
        }

        const [monthStr, yearStr] = value.split('/');
        const currentDate = new Date();
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);
        const expiryDate = new Date(year + 2000, month - 1, 1);
        const expiryLastDate = new Date(expiryDate.getFullYear(), expiryDate.getMonth() + 1, 0);
        return expiryLastDate > currentDate;
      },
      { message: mapLocaleToMeaningfulFormat().invalidExpDate },
    )
    .optional(),
  securityCode: z
    .string()
    .refine(
      (value) => {
        const cleanedValue = value.replace(/\D/g, '');
        const length = cleanedValue.length;
        switch (cardType) {
          case mapLocaleToMeaningfulFormat().visa:
          case mapLocaleToMeaningfulFormat().mastercard:
          case mapLocaleToMeaningfulFormat().discover:
            return length === 3;
          case mapLocaleToMeaningfulFormat().amex:
            return length === 4;
          default:
            return false;
        }
      },
      { message: mapLocaleToMeaningfulFormat().securityCode },
    )
    .optional(),
  ssn: z
    .string()
    .refine(
      (value) => {
        const cleanedValue = value.replace(/\D/g, '');
        const length = cleanedValue.length;
        return length === 4;
      },
      { message: mapLocaleToMeaningfulFormat().ssnNumber },
    )
    .optional(),
  creditAccountId: z
    .string()
    .min(1, { message: mapLocaleToMeaningfulFormat().customerAccountNumberMessage })
    .optional(),
  dob: z
    .string()
    .refine(
      (value) => {
        const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
        if (!dobRegex.test(value)) {
          return false;
        }

        const [month, day, year] = value.split('/').map(Number);
        const dobDate = new Date(year, month - 1, day);

        if (isNaN(dobDate.getTime())) {
          return false;
        }

        const lastDay = new Date(year, month, 0).getDate();
        if (day > lastDay) {
          return false;
        }

        const currentDate = new Date();
        return dobDate < currentDate;
      },
      { message: mapLocaleToMeaningfulFormat().validDOB },
    )
    .optional(),
  checkDefaultAddress: z.boolean().optional(),
  dpqDOB: z
    .string()
    .refine(
      (value) => {
        const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
        if (!dobRegex.test(value)) {
          return false;
        }
        const [month, day, year] = value.split('/').map(Number);
        const dobDate = new Date(year, month - 1, day);
        if (isNaN(dobDate.getTime())) {
          return false;
        }
        const lastDay = new Date(year, month, 0).getDate();
        if (day > lastDay) {
          return false;
        }
        const minValidDate = new Date();
        minValidDate.setFullYear(minValidDate.getFullYear() - 21);
        return dobDate <= minValidDate;
      },
      { message: mapLocaleToMeaningfulFormat().validDOB },
    )
    .optional(),
  creditAccountIdOptional: z.string().optional(),
  requestPurpose: z.string().min(1, { message: mapLocaleToMeaningfulFormat().selectRequestPurpose }).max(50).optional(),
  // shippingAddress: z.string().optional(),
});
